import React, { useEffect, useState } from 'react'
import PageHeader from '../../../componentsII/PageHeader'
import Loader from "../../../componentsII/Loader";
import { ArrowRight, X, PenBoxIcon } from 'lucide-react';
import { Modal, Box, Typography, Card } from '@mui/material';
import AddReductionTarget from '../../../components/ReductionTargetModelling/AddReductionTarget';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { useSelector } from 'react-redux';
import api from '../../../axios/api'


const ReductionTargetModeling = () => {
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [companyData, setCompanyData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [baseYear, setBaseYear] = useState(null);
  const [targetYear, setTargetYear] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);

  const [targetYear1, setTargetYear1] = useState(null);
  const [availableYears1, setAvailableYears1] = useState([]);

  const [isAddReductionTargetModalOpen, setIsAddReductionTargetModalOpen] = useState(false);
  
  const user = useSelector((state) => state.user.user);

  const getReductionTargetData = async () => {
    try {
      setIsDataLoading(true);
      const response = await api.get(`/analytics/reductionTargetModelling`, {
        params: {
          companyId: user.company.id
        }
      });
      
      // Process data from API response
      const data = response.data || [];
      setCompanyData(data);
      
      // Find base year, target year, and selected year
      const baseYearData = data.find(year => year.is_base_year === "1") || null;
      const targetYearData = data.find(year => year.is_target_year === "1") || null;
      const selectedYearData = data.find(year => year.is_selected_year === 1) || null;
      
      // Set the available years (excluding base year)
      const otherYears = data.filter(year => year.is_base_year !== "1");
      
      setBaseYear(baseYearData);
      setTargetYear(targetYearData);
      setSelectedYear(selectedYearData);
      setAvailableYears(otherYears);
      
      // Generate chart data
      if (baseYearData && targetYearData) {
        generateChartData(data, baseYearData, targetYearData);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsDataLoading(false);
    }
  };

  // Adding back getReductionYearPercentage for the AddReductionTarget component
  const getReductionYearPercentage = async () => {
    try {
      setIsDataLoading(true);
      const response = await api.get(`/company/companyDataYear?company_id=${user.company.id}`);
      const baseYear = response.data.data.find(year => year.is_base_year == "1") || [];
      const targetYear = response.data.data.find(year => year.is_target_year == "1") || [];
      const otherYears = response.data.data.filter(year => year.is_base_year !== "1");

      setAvailableYears1(otherYears);
      setBaseYear(baseYear);
      setTargetYear1(targetYear);
    } catch (error) {
      console.log(error)
    } finally {
      setIsDataLoading(false);
    }
  }

  const generateChartData = (data, baseYearData, targetYearData) => {
    // Sort data by year
    const sortedData = [...data].sort((a, b) => a.start_year - b.start_year);
    
    // Get the base emission value
    const baseEmission = baseYearData.total_co2_emission_tonnes;
    
    // Calculate target emissions reduction per year
    const startYear = parseInt(baseYearData.start_year);
    const endYear = parseInt(targetYearData.start_year);
    const yearDiff = endYear - startYear;
    const reductionPercentage = parseFloat(targetYearData.target_reduction_percentage);
    const targetEmission = baseEmission * (1 - reductionPercentage / 100);
    
    // Generate chart data points
    const chartPoints = [];
    
    for (let year = startYear; year <= endYear; year++) {
      const yearData = sortedData.find(d => parseInt(d.start_year) === year);
      
      // Calculate the linear reduction target for this year
      const yearIndex = year - startYear;
      const reductionFactor = yearIndex / yearDiff;
      const targetForYear = baseEmission - (reductionFactor * (baseEmission - targetEmission));
      
      chartPoints.push({
        year: year.toString(),
        emissions: yearData ? yearData.total_co2_emission_tonnes : null,
        target: parseFloat(targetForYear.toFixed(2))
      });
    }
    
    setChartData(chartPoints);
  };

  useEffect(() => {
    getReductionYearPercentage();
    getReductionTargetData();
  }, []);

  // Calculate the target reduction amount (the amount that needs to be reduced)
  const calculateReductionTarget = () => {
    if (baseYear && targetYear) {
      const baseEmission = baseYear.total_co2_emission_tonnes;
      const reductionPercentage = targetYear.target_reduction_percentage;
      // Target reduction is simply the percentage of base year emissions
      return (baseEmission * reductionPercentage / 100).toFixed(2);
    }
    return 0;
  };

  return (
    <div>
      <PageHeader
        title="Reduction Target Modeling"
        text={[
          "Your reduction target model is utilizing your scope 1,2 and 3 emissions. Updates to your",
          "footprints will be reflected below as you complete activities to calculate your emissions."
        ]}
      >
        {isDataLoading ? (
          <div className="w-full h-[200px] flex justify-center items-center">
            <Loader />
          </div>
        ) : !targetYear ? (
          <div className="text-center py-16 bg-gray-50 rounded-lg">
            <img
              src="/vectors/planning.jpg"
              alt="No Data"
              className="w-80 h-80 mx-auto text-gray-400 mb-4"
            />
            <h3 className="text-xl font-medium text-gray-600 mb-2">
              Create your own reduction target model
            </h3>
            <p className="text-gray-500">
              Set your target year and how much you want to reduce your emissions by, and we will calculate the rest.
            </p>

            <div className="flex justify-center mt-10">
              <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2" onClick={() => setIsAddReductionTargetModalOpen(true)}>
                Create Target <ArrowRight />
              </button>
            </div>
          </div>
        ) : (
          <div className='bg-gray-50 p-4 rounded-lg'>
            <div>
              <div className="flex justify-between items-center pt-5">
                <Typography variant="h5" fontWeight="bold">
                  Targeting a {targetYear.target_reduction_percentage}% reduction by {targetYear.start_year}
                </Typography>
                <button className="bg-green-600 hover:bg-green-700 text-white flex gap-2 p-2 rounded-lg" onClick={() => { setIsAddReductionTargetModalOpen(true) }}>
                  <PenBoxIcon size={16} title="Edit" color="white" className='mt-1' /> Edit Target
                </button>
              </div>

              <Typography variant="body" className="pt-5 text-gray-500">
                You can edit your reduction target model at any time to adjust and see different possibilities for reaching your net-zero goal.
              </Typography>
            </div>

            <div className='mt-4 flex gap-4'>
              <Card className="w-1/5 p-6">
                <div>
                  <Typography variant='h5' fontWeight="bold">Current Emissions</Typography>
                  <Typography variant="body" className='pt-5' color='grey'>{selectedYear?.start_year} reporting year</Typography>
                </div>
                <div className='flex items-center gap-1'>
                  <Typography variant="h5" fontWeight="bold" className='pt-5'>{selectedYear?.total_co2_emission_tonnes.toFixed(2)}</Typography> 
                  <Typography variant="body" className='pt-5' color='grey'>tCO₂e</Typography>
                </div>
              </Card>

              <Card className="w-1/5 p-6">
                <div>
                  <Typography variant='h5' fontWeight="bold">Base Year</Typography>
                  <Typography variant="body" className='pt-5' color='grey'>Disclosure period start year</Typography>
                </div>
                <div className='flex items-center gap-1'>
                  <Typography variant="h5" fontWeight="bold" className='pt-5'>{baseYear?.start_year}</Typography>
                </div>
              </Card>

              <Card className="w-1/5 p-6">
                <div>
                  <Typography variant='h5' fontWeight="bold">Target Year</Typography>
                  <Typography variant="body" className='pt-5' color='grey'>Near-Term target</Typography>
                </div>
                <div className='flex items-center gap-1'>
                  <Typography variant="h5" fontWeight="bold" className='pt-5'>{targetYear?.start_year}</Typography>
                </div>
              </Card>

              <Card className="w-1/5 p-6">
                <div>
                  <Typography variant='h5' fontWeight="bold">Reduction Ambition</Typography>
                  <Typography variant="body" className='pt-5' color='grey'>Annual Reduction Percentage</Typography>
                </div>
                <div className='flex items-center gap-1'>
                  <Typography variant="h5" fontWeight="bold" className='pt-5'>{targetYear?.target_reduction_percentage}</Typography> 
                  <Typography variant="body" className='pt-5' color='grey'>%</Typography>
                </div>
              </Card>

              <Card className="w-1/5 p-6">
                <div>
                  <Typography variant='h5' fontWeight="bold">Target Reduction</Typography>
                  <Typography variant="body" className='pt-5' color='grey'>Total emissions to reduce</Typography>
                </div>
                <div className='flex items-center gap-1'>
                  <Typography variant="h5" fontWeight="bold" className='pt-5'>{calculateReductionTarget()}</Typography> 
                  <Typography variant="body" className='pt-5' color='grey'>tCO₂e</Typography>
                </div>
              </Card>
            </div>

            <div className="flex items-start gap-8 mt-10">
              <div className="flex-1">
                <div className="w-2/3 h-80">
                  <div className="w-full h-[400px] p-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={chartData}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 20
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis
                          dataKey="year"
                          stroke="#666"
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis
                          stroke="#666"
                          tick={{ fontSize: 12 }}
                          domain={[0, 'auto']}
                          tickCount={8}
                          label={{
                            value: 'Total Carbon Emissions (tCO₂e)',
                            angle: -90,
                            position: 'insideLeft',
                            style: { textAnchor: 'middle' }
                          }}
                        />
                        <Tooltip
                          formatter={(value, name) => [
                            value ? `${value.toFixed(2)} tCO₂e` : 'Data not available',
                            name === 'emissions' ? 'Actual Emissions' : 'Target Level'
                          ]}
                        />
                        <Legend
                          verticalAlign="top"
                          height={36}
                          formatter={(value) => (
                            value === 'emissions' ? 'Actual Emissions' : 'Target Level'
                          )}
                        />
                        <Line
                          type="monotone"
                          dataKey="emissions"
                          stroke="#40E0D0"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          name="emissions"
                          connectNulls={true}
                        />
                        <Line
                          type="monotone"
                          dataKey="target"
                          stroke="#666"
                          strokeDasharray="5 5"
                          dot={{ r: 4 }}
                          name="target"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </PageHeader>

      <Modal
        open={isAddReductionTargetModalOpen}
        onClose={() => setIsAddReductionTargetModalOpen(false)}
        aria-labelledby="add-location-modal-title"
        aria-describedby="add-location-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "auto",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            display: "relative",
          }}
        >
          <button
            onClick={() => {
              setIsAddReductionTargetModalOpen(false)
            }}
            className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
            aria-label="Close"
          >
            <X size={24} />
          </button>
          <AddReductionTarget
            availableYears={availableYears1}
            TargetYear={targetYear1}
            onClose={() => {
              setIsAddReductionTargetModalOpen(false)
            }}
            refetch={() => {
              getReductionYearPercentage();
              getReductionTargetData();
            }}
          />
        </Box>
      </Modal>
    </div>
  );
};

export default ReductionTargetModeling;